import { useVisiblePolling } from '@/utils/useVisiblePolling';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/config';
import { MessageCircle, Send, Trash2, RefreshCw } from 'lucide-react';

const POLL_INTERVAL = 5000;
const LIST_POLL_INTERVAL = 60000;

const LiveChatAdmin = ({ token, i18n }) => {
  const isAr = i18n?.language === 'ar';
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const listRef = useRef(null);
  const pollRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchConversations = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/chat/conversations`, { headers });
      setConversations(data.conversations || []);
    } catch (e) {
      console.error(e);
    }
  }, [token]);

  const fetchDetail = useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await axios.get(`${API}/admin/chat/conversations/${id}`, { headers });
      setDetail(data);
    } catch (e) {
      console.error(e);
    }
  }, [token]);

  // Initial list + polling
  useEffect(() => {
    setLoading(true);
    fetchConversations().finally(() => setLoading(false));
  }, [fetchConversations]);
  useVisiblePolling(fetchConversations, LIST_POLL_INTERVAL);

  // Poll selected conversation
  useEffect(() => {
    if (!selectedId) return undefined;
    fetchDetail(selectedId);
    return undefined;
  }, [selectedId, fetchDetail]);
  useVisiblePolling(() => selectedId && fetchDetail(selectedId), POLL_INTERVAL, !!selectedId);

  // Auto-scroll to bottom ONLY when the admin is already near the bottom.
  // Prevents snapping-away when the admin scrolls up to read older messages
  // while polling keeps refreshing `detail` every few seconds.
  const wasAtBottomRef = useRef(true);
  const handleScroll = () => {
    const el = listRef.current;
    if (!el) return;
    wasAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };
  useEffect(() => {
    if (listRef.current && wasAtBottomRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [detail]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = draft.trim();
    if (!text || !selectedId || sending) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/admin/chat/conversations/${selectedId}/reply`,
        { text },
        { headers }
      );
      setDraft('');
      fetchDetail(selectedId);
      fetchConversations();
    } catch (err) {
      toast.error(isAr ? 'فشل الإرسال' : 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const typingSentRef = useRef(0);
  const handleDraftChange = (e) => {
    const v = e.target.value;
    setDraft(v);
    if (selectedId && v) {
      const now = Date.now();
      if (now - typingSentRef.current > 2500) {
        typingSentRef.current = now;
        axios.post(`${API}/admin/chat/conversations/${selectedId}/typing`, {}, { headers }).catch(() => {});
      }
    }
  };

  const remove = async (id) => {
    if (!window.confirm(isAr ? 'حذف هذه المحادثة نهائياً؟' : 'Delete this conversation permanently?')) return;
    try {
      await axios.delete(`${API}/admin/chat/conversations/${id}`, { headers });
      if (selectedId === id) { setSelectedId(null); setDetail(null); }
      fetchConversations();
    } catch (err) {
      toast.error(isAr ? 'فشل الحذف' : 'Delete failed');
    }
  };

  const fmt = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString(isAr ? 'ar-EG' : 'sv-SE', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' });
    } catch { return ''; }
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-md overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-emerald-500" />
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
            {isAr ? 'المحادثات المباشرة' : 'Live Chat Conversations'}
          </h2>
          <span className="text-xs text-slate-500">({conversations.length})</span>
        </div>
        <button
          type="button"
          onClick={fetchConversations}
          className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
          aria-label="refresh"
          data-testid="chat-admin-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] h-[calc(100vh-220px)] min-h-[500px] max-h-[750px]">
        {/* Left: conversation list — hidden on mobile when a conversation is selected so the reply form stays visible */}
        <div className={`border-e border-slate-200 dark:border-slate-700 overflow-y-auto ${selectedId ? 'hidden md:block' : 'block'}`}>
          {conversations.length === 0 && !loading && (
            <div className="p-6 text-center text-sm text-slate-500">
              {isAr ? 'لا توجد محادثات بعد' : 'No conversations yet'}
            </div>
          )}
          {conversations.map((c) => {
            const active = selectedId === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setSelectedId(c.id)}
                className={`w-full text-start px-4 py-3 border-b border-slate-100 dark:border-slate-800 transition ${
                  active ? 'bg-emerald-50 dark:bg-emerald-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                }`}
                data-testid={`chat-admin-convo-${c.id}`}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-semibold text-sm text-slate-900 dark:text-slate-100 truncate">
                    {c.visitor_name || (isAr ? 'زائر' : 'Visitor')}
                    <span className="text-slate-400 font-normal"> → {c.agent_name}</span>
                  </span>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {c.human_requested && (
                      <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 text-[9px] font-bold">
                        SOS
                      </span>
                    )}
                    {c.unread_by_admin && (
                      <span className="w-2 h-2 rounded-full bg-red-500" />
                    )}
                  </div>
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                  {c.last_message?.text || (isAr ? '— لا توجد رسائل' : '— no messages')}
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5">{fmt(c.last_message_at)}</div>
              </button>
            );
          })}
        </div>

        {/* Right: message thread */}
        <div className={`flex flex-col bg-slate-50 dark:bg-slate-950 min-h-0 ${!selectedId ? 'hidden md:flex' : 'flex'}`}>
          {!selectedId ? (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-500">
              {isAr ? 'اختر محادثة لعرضها' : 'Select a conversation to view'}
            </div>
          ) : (
            <>
              <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => { setSelectedId(null); setDetail(null); }}
                    className="md:hidden p-1.5 -ms-1 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-900/40 flex-shrink-0"
                    aria-label="back"
                    data-testid="chat-admin-back"
                  >
                    <span className="text-emerald-700 dark:text-emerald-300 text-xl leading-none">{isAr ? '›' : '‹'}</span>
                  </button>
                  <div className="min-w-0 flex-1">
                  <div className="text-[11px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300 font-bold">
                    {isAr ? 'رد باسم' : 'Reply as'}
                  </div>
                  <div className="font-bold text-lg text-slate-900 dark:text-slate-100">
                    {detail?.agent_name || '—'}
                  </div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                    {isAr ? 'الزائر' : 'Visitor'}: <span className="font-semibold">{detail?.visitor_name || '—'}</span>
                    {' · '}
                    {isAr ? 'لغة' : 'lang'}: {detail?.user_lang || '—'}
                    {detail?.human_requested && (
                      <span className="ml-2 px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 text-[10px] font-bold">
                        {isAr ? 'طلب دعم بشري' : 'HUMAN REQUESTED'}
                      </span>
                    )}
                  </div>
                </div>
                </div>
                <button
                  type="button"
                  onClick={() => remove(selectedId)}
                  className="p-2 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                  data-testid="chat-admin-delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div ref={listRef} onScroll={handleScroll} className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-2">
                {(detail?.messages || []).map((m) => {
                  if (m.sender === 'system') {
                    return (
                      <div key={m.id} className="flex justify-center">
                        <div className="text-[11px] text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-full px-3 py-1">
                          {m.text}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={m.id} className={`flex ${m.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[70%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                          m.sender === 'admin'
                            ? 'bg-emerald-500 text-white rounded-br-sm'
                            : m.sender === 'assistant'
                              ? 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-bl-sm italic'
                              : 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-bl-sm'
                        }`}
                      >
                        {m.sender === 'assistant' && (
                          <div className="text-[10px] font-bold mb-0.5 opacity-70">🤖 AI</div>
                        )}
                        {m.text}
                        <div className="text-[10px] opacity-70 mt-1">{fmt(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
                {detail?.user_typing && (
                  <div className="flex justify-start">
                    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl rounded-bl-sm px-3 py-2 flex items-center gap-1">
                      <span className="text-[10px] text-slate-500 mr-1">{detail?.visitor_name} {isAr ? 'يكتب' : 'is typing'}</span>
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                )}
              </div>
              <form onSubmit={send} className="flex-shrink-0 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
                {/* Quick reply templates */}
                <div className="flex items-center gap-1.5 px-3 pt-2 pb-1 overflow-x-auto">
                  {(() => {
                    const agent = detail?.agent_name || '';
                    const vname = detail?.visitor_name || '';
                    const lang = detail?.user_lang || 'sv';
                    const T = {
                      ar: [
                        { label: '👋 ترحيبة', text: `مرحباً ${vname}! أنا ${agent} من فريق دعم DriveMaster. كيف يمكنني مساعدتك؟` },
                        { label: '💳 الأسعار', text: 'اشتراكاتنا: شهر 249 كرونا · 6 أشهر بسعر مخفّض · سنة كاملة أوفر خيار. تشمل جميع الاشتراكات كل الميزات.' },
                        { label: '❓ الاختبار', text: 'الاختبار المجاني متاح لرخصة B ولرخصة التاكسي. تحصل على الاختبارات الكاملة (+800 سؤال) بالاشتراك.' },
                        { label: '🌐 اللغات', text: 'نوفّر 9 لغات: السويدية، الإنجليزية، العربية، التركية، الفارسية، الصومالية، البولندية، الإسبانية، والألمانية.' },
                        { label: '🙏 شكراً', text: 'شكراً لتواصلك معنا! إذا احتجت أي شيء آخر لا تتردد في الرد هنا.' },
                      ],
                      sv: [
                        { label: '👋 Välkommen', text: `Hej ${vname}! Jag är ${agent} från DriveMaster-supporten. Hur kan jag hjälpa dig?` },
                        { label: '💳 Priser', text: 'Våra abonnemang: 1 månad 249 kr · 6 månader med rabatt · 1 år bästa värde. Alla planer inkluderar allt.' },
                        { label: '❓ Provet', text: 'Gratisprovet finns för både B-körkort och Taxi. Fulla prov (+800 frågor) ingår i abonnemang.' },
                        { label: '🌐 Språk', text: 'Vi stöder 9 språk: svenska, engelska, arabiska, turkiska, persiska, somaliska, polska, spanska och tyska.' },
                        { label: '🙏 Tack', text: 'Tack för att du kontaktar oss! Skriv gärna igen om du behöver mer hjälp.' },
                      ],
                      en: [
                        { label: '👋 Welcome', text: `Hi ${vname}! I'm ${agent} from the DriveMaster support team. How can I help you?` },
                        { label: '💳 Pricing', text: 'Our plans: 1 month 249 SEK · 6 months discounted · 1 year best value. All plans include everything.' },
                        { label: '❓ The test', text: 'Free tier available for both B-license and Taxi. Full tests (+800 questions) come with any subscription.' },
                        { label: '🌐 Languages', text: 'We support 9 languages: Swedish, English, Arabic, Turkish, Farsi, Somali, Polish, Spanish, and German.' },
                        { label: '🙏 Thanks', text: 'Thanks for reaching out! Feel free to reply here if you need anything else.' },
                      ],
                    };
                    const tmpls = T[lang] || T.en;
                    return tmpls.map((tpl) => (
                      <button
                        key={tpl.label}
                        type="button"
                        onClick={() => setDraft(tpl.text)}
                        className="flex-shrink-0 text-[11px] px-2.5 py-1 rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 font-semibold transition whitespace-nowrap"
                        data-testid={`quick-reply-${tpl.label}`}
                      >
                        {tpl.label}
                      </button>
                    ));
                  })()}
                </div>
                <div className="flex items-center gap-2 p-3 pt-1">
                  <input
                    type="text"
                    value={draft}
                    onChange={handleDraftChange}
                    placeholder={isAr ? `اكتب باسم ${detail?.agent_name || ''}...` : `Reply as ${detail?.agent_name || ''}...`}
                    className="flex-1 px-3 py-2 rounded-full border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    disabled={sending}
                    data-testid="chat-admin-input"
                  />
                  <button
                    type="submit"
                    disabled={sending || !draft.trim()}
                    className="w-10 h-10 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white flex items-center justify-center transition"
                    data-testid="chat-admin-send"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveChatAdmin;
